import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "../ui/card";
import {
  Car,
  ArrowRight,
  Loader,
  AlertCircle,
  Check,
  MapPin,
  Hash,
  Gauge,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";

const ExtendedWarrantyVehicleSelection = ({ onComplete, onNoVehicles }) => {
  const { user, getIdToken } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Helpers --------------------------------------------------------------
  const toStr = (v) => (typeof v === "string" ? v.trim() : v);

  const normalize = (raw = {}) => {
    const v = raw || {};
    const year = v.year ?? v.Year ?? null;
    const make = toStr(v.make ?? v.Make ?? v.brand ?? null);
    const model = toStr(v.model ?? v.Model ?? null);
    const state = toStr(v.state ?? v.State ?? v.registrationState ?? null);
    const plate = toStr(
      v.license ?? v.licensePlate ?? v.plate ?? v.registrationNumber ?? null
    );
    const vin = toStr(v.vin ?? v.VIN ?? v.vinNumber ?? null);
    const mileage =
      typeof v.mileage === "number"
        ? v.mileage
        : typeof v.mileage === "string"
        ? Number(v.mileage.replace(/[,\s]/g, "")) || null
        : null;
    const source = v.source ?? v.Source ?? null;
    const id = v.id ?? v.vehicleId ?? v._id ?? null;

    return {
      id,
      year,
      make,
      model,
      state,
      plate,
      vin,
      mileage,
      source,
      _raw: v,
    };
  };

  const lastVin = (vin) => (vin ? vin.slice(-6).toUpperCase() : null);

  const titleFor = (n) => {
    // Prefer Year Make Model
    const y = n.year ? String(n.year) : null;
    const base = [y, n.make, n.model].filter(Boolean).join(" ");
    if (base) return base;

    // Next best: Make + Model
    const mm = [n.make, n.model].filter(Boolean).join(" ");
    if (mm) return mm;

    // Next: Model only
    if (n.model) return n.model;

    // Next: VIN (last 6)
    if (n.vin) return `VIN • …${lastVin(n.vin)}`;

    // Next: Plate or State
    if (n.plate && n.state) return `${n.plate} • ${n.state}`;
    if (n.plate) return n.plate;
    if (n.state) return `Vehicle in ${n.state}`;

    // Fallback
    return "Vehicle";
  };

  const subtitleFor = (n) => {
    const bits = [];
    if (n.plate) bits.push(n.plate);
    if (n.state) bits.push(n.state);
    if (n.mileage != null) bits.push(`${n.mileage.toLocaleString()} miles`);
    return bits.join(" • ");
  };

  const keyFor = (n, idx) =>
    n.id || n.vin || n.plate || `${n.make}-${n.model}-${n.year}-${idx}`;
  const sameVehicle = (a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    if (a.vin && b.vin) return a.vin === b.vin;
    if (a.plate && b.plate) return a.plate === b.plate;
    return a === b; // fallback to ref equality
  };

  // --- Data fetch -----------------------------------------------------------
  useEffect(() => {
    const fetchVehicleCandidates = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = await getIdToken();
        if (!token) throw new Error("Authentication required");
        //"x-user-id": "CUST_jbvHfKTkTYkG",

        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/vehicle/candidates`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
            },
          }
        );

        if (!response.ok)
          throw new Error(`Failed to fetch vehicles: ${response.status}`);

        const vehicleData = await response.json();
        let normalized = (Array.isArray(vehicleData) ? vehicleData : []).map(
          normalize
        );
        //normalized = [];
        setVehicles(normalized);
        console.log(onNoVehicles);
        if (normalized.length === 0 && onNoVehicles) {
          //onNoVehicles();
        }
      } catch (err) {
        console.error("Error fetching vehicle candidates:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchVehicleCandidates();
    } else {
      setIsLoading(false);
      setError("Please log in to view your vehicles");
    }
  }, [user, getIdToken, onNoVehicles]);

  // --- Handlers -------------------------------------------------------------
  const handleVehicleSelect = (vehicle) => setSelectedVehicle(vehicle);

  const handleContinue = async () => {
    if (!selectedVehicle) {
      setError("Please select a vehicle to continue");
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      sessionStorage.setItem(
        "selectedVehicle",
        JSON.stringify(selectedVehicle)
      );
      onComplete?.(selectedVehicle);
    } catch (err) {
      console.error("Error proceeding with selected vehicle:", err);
      setError("Failed to proceed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- UI States ------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Car className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Loading Your Vehicles
          </h1>
          <p className="text-gray-600">
            We're checking for vehicles in your account...
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 flex justify-center">
            <div className="flex items-center gap-2">
              <Loader className="w-5 h-5 animate-spin text-blue-500" />
              <span className="text-gray-600">
                Fetching vehicle information...
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!error && vehicles.length === 0) {
    return (
      <div className="space-y-8 p-4 pt-5 pb-6">
        <div className="text-center bg-white p-5 rounded-[20px] space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <Car className="w-8 h-8 text-gray-400" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900">
            No vehicles found
          </h1>

          <h2 className="text-lg text-gray-600">
            No worries! You can create your vehicle now by adding your proof of
            insurance.
          </h2>
        </div>

        <button
          onClick={onNoVehicles}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          Add Vehicle
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Something Went Wrong
          </h1>
          <p className="text-gray-600">
            We encountered an error while loading your vehicles.
          </p>
        </div>

        <Card className="border-red-100 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error Details</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <button
          onClick={() => window.location.reload()}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <Car className="w-8 h-8 text-blue-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Select Your Vehicle
        </h1>
        <p className="text-gray-600">
          We found {vehicles.length} vehicle{vehicles.length > 1 ? "s" : ""} in
          your account. Please select the one you'd like extended warranty
          coverage for.
        </p>
      </div>

      <div className="space-y-4">
        {vehicles.map((n, idx) => {
          console.log(n);
          const isSelected = sameVehicle(selectedVehicle, n);
          const title = titleFor(n);
          const sub = subtitleFor(n);
          return (
            <Card
              key={keyFor(n, idx)}
              className={`cursor-pointer transition-all border-2 ${
                isSelected
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
              }`}
              onClick={() => handleVehicleSelect(n)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                      isSelected
                        ? "border-blue-600 bg-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">
                          {title}
                        </h3>

                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600">
                          {(n.plate || n.state) && (
                            <div className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              <span>{n.plate || "No plate"}</span>
                            </div>
                          )}

                          {n.state && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>{n.state}</span>
                            </div>
                          )}

                          {n.mileage != null && (
                            <div className="flex items-center gap-1">
                              <Gauge className="w-3 h-3" />
                              <span>{n.mileage.toLocaleString()} miles</span>
                            </div>
                          )}

                          {n.vin && (
                            <div className="col-span-2 mt-1">
                              <div className="text-xs text-gray-500">VIN</div>
                              <div className="text-sm break-all select-all">
                                {n.vin}
                              </div>
                            </div>
                          )}
                        </div>

                        {(n.source || n.vin || n.plate) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {n.source && (
                              <span
                                className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                  n.source === "MANUAL"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {n.source === "MANUAL" ? n.source : n.source}
                              </span>
                            )}
                            {/* {n.vin && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                                VIN …{lastVin(n.vin)}
                              </span>
                            )}
                            {n.plate && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                                Plate {n.plate}
                              </span>
                            )} */}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {error && !isLoading && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      <button
        onClick={handleContinue}
        disabled={!selectedVehicle || isSubmitting}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium ${
          selectedVehicle && !isSubmitting
            ? "bg-blue-600 text-white hover:bg-blue-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {isSubmitting ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <span>Continue with Selected Vehicle</span>
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <p className="text-xs text-center text-gray-500">
        Showing {vehicles.length} vehicle{vehicles.length > 1 ? "s" : ""} from
        your account
      </p>
    </div>
  );
};

export default ExtendedWarrantyVehicleSelection;
